import { ReactNode, Fragment } from 'react';
import styled, { css } from 'styled-components';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors, s24, s4 } from '@ardoq/design-tokens';
import {
  FieldWrapper,
  FormSize,
  HelperTextWrapper,
  InputErrorMessage,
  InputWarningMessage,
} from '@ardoq/forms';

const SwitchContainer = styled.div<{ $isDisabled?: boolean }>`
  display: flex;
  vertical-align: middle;
  width: 80px;
  height: ${s24};
  border-radius: 100px;
  background-color: ${colors.surfaceStatusNeutral};
  gap: ${s4};
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      background-color: ${colors.surfaceDisabled};
    `}
`;

const Radio = styled.input`
  display: none;
`;

const getColorCss = (isSelected: boolean, switchPosition: SwitchPosition) => {
  if (!isSelected) return '';
  switch (switchPosition) {
    case 'rejected':
      return css`
        color: ${colors.textOnInvertedStrong};
        background-color: ${colors.iconError};
      `;
    case 'approved':
      return css`
        color: ${colors.textOnInvertedStrong};
        background-color: ${colors.iconSuccess};
      `;
    case 'pending':
      return css`
        background-color: ${colors.iconOnInverted};
      `;
  }
};

const Label = styled.label<{
  $isSelected: boolean;
  $switchPosition: SwitchPosition;
  $isDisabled?: boolean;
}>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${s24};
  height: ${s24};
  border-radius: 100%;
  transition: all 0.3s ease-in-out;
  color: ${colors.textSubtle};
  ${({ $isSelected, $switchPosition }) =>
    getColorCss($isSelected, $switchPosition)}
  ${({ $isSelected }) =>
    !$isSelected &&
    css`
      &:hover {
        background-color: ${colors.surfaceActionGhostHover};
        color: ${colors.textSubtle};
      }
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${colors.textDisabled};
    `}
`;

export type SwitchPosition = 'rejected' | 'pending' | 'approved';

const switchPositions: SwitchPosition[] = ['rejected', 'pending', 'approved'];

const getIconForSwitchPosition = (switchPosition: SwitchPosition) => {
  switch (switchPosition) {
    case 'rejected':
      return IconName.CLOSE;
    case 'approved':
      return IconName.CHECK_MARK;
    case 'pending':
      return IconName.REMOVE;
  }
};

type TriStateSwitchProps = {
  name: string;
  value: SwitchPosition;
  isDisabled?: boolean;
  helperText?: ReactNode;
  dataTestId?: string;
  formSize?: FormSize;
  alignItemsCenter?: boolean;
  errorMessage?: string;
  warningMessage?: string;
  onChange: (value: SwitchPosition) => void;
};

export const TriStateSwitch = ({
  name,
  value,
  dataTestId,
  formSize,
  alignItemsCenter,
  helperText,
  errorMessage,
  warningMessage,
  isDisabled,
  onChange,
}: TriStateSwitchProps) => {
  return (
    <FieldWrapper
      data-test-id={dataTestId}
      $formSize={formSize}
      $alignItemsCenter={alignItemsCenter}
    >
      {helperText && <HelperTextWrapper>{helperText}</HelperTextWrapper>}
      <SwitchContainer $isDisabled={isDisabled}>
        {switchPositions.map(switchPosition => (
          <Fragment key={`${name}-${switchPosition}-${value}`}>
            <Radio
              onChange={() => onChange(switchPosition)}
              name={name}
              id={`${name}-${switchPosition}-${value}`}
              type="radio"
              value={switchPosition}
            />
            <Label
              $isSelected={value === switchPosition}
              htmlFor={`${name}-${switchPosition}-${value}`}
              $switchPosition={switchPosition}
              $isDisabled={isDisabled}
            >
              <Icon
                iconSize={IconSize.SMALL}
                iconName={getIconForSwitchPosition(switchPosition)}
              />
            </Label>
          </Fragment>
        ))}
      </SwitchContainer>
      {errorMessage && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
      {!errorMessage && warningMessage && (
        <InputWarningMessage>{warningMessage}</InputWarningMessage>
      )}
    </FieldWrapper>
  );
};
